import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input } from "rsuite";

/**
 * Modal to update the stock count for a single (branch, medicine) pair.
 * Calls POST /update-branch-stock — see FRONTEND_API_INTEGRATION.md §10.4.
 *
 * Props:
 *  - isOpen      : boolean
 *  - record      : the BranchStock row being edited:
 *                  { _id, branch: {_id,name}|string, medicine: {_id,name,qty,cost}, stock }
 *  - submitting  : boolean (parent controls the saving state)
 *  - onClose     : () => void
 *  - onSave      : ({ branchId, medicineId, stock }) => Promise<void>
 */
const UpdateStockModal = ({
  isOpen,
  record,
  submitting = false,
  onClose,
  onSave,
}) => {
  const [stock, setStock] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setStock(record?.stock != null ? String(record.stock) : "0");
      setError("");
    }
  }, [isOpen, record]);

  const branchName =
    typeof record?.branch === "object"
      ? record?.branch?.name
      : record?.branchName || "—";
  const medicineName = record?.medicine?.name || record?.medicineName || "—";
  const medicineQty = record?.medicine?.qty || "";
  const medicineCost = record?.medicine?.cost;

  const handleSubmit = async () => {
    const value = Number(stock);
    if (stock === "" || Number.isNaN(value) || value < 0) {
      setError("Enter a stock count of 0 or greater.");
      return;
    }
    const branchId =
      (typeof record?.branch === "object" ? record?.branch?._id : record?.branch) ||
      record?.branchId;
    const medicineId = record?.medicine?._id || record?.medicineId;
    if (!branchId || !medicineId) {
      setError("Missing branch or medicine reference. Please refresh.");
      return;
    }
    await onSave({ branchId, medicineId, stock: value });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} backdrop={submitting ? "static" : true} size="sm">
      <Modal.Header>
        <Modal.Title>Update stock</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "12px 14px",
            marginBottom: 16,
            fontSize: "0.9rem",
            color: "#334155",
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <strong style={{ color: "#0f172a" }}>Branch:</strong> {branchName}
          </div>
          <div>
            <strong style={{ color: "#0f172a" }}>Medicine:</strong> {medicineName}
            {medicineQty ? ` · ${medicineQty}` : ""}
            {medicineCost != null ? ` · Rs ${medicineCost}` : ""}
          </div>
        </div>

        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Stock count</Form.ControlLabel>
            <Input
              type="number"
              min={0}
              value={stock}
              onChange={(value) => {
                setStock(value);
                if (error) setError("");
              }}
              placeholder="0"
            />
            {error && (
              <div style={{ color: "#dc2626", fontSize: "0.85rem", marginTop: 4 }}>
                {error}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          style={{ backgroundColor: "#198754", color: "white" }}
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Save stock"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateStockModal;
